import { eq } from '@tanstack/db'
import { useLiveQuery } from '@tanstack/react-db'
import { useLocalSearchParams } from 'expo-router'
import { useCurrentOrg } from '@/hooks/useCurrentOrg'
import { findOrCreateAddress, isNonBlankAddress } from '@/lib/address'
import { performMutations, useMutation } from '@/lib/mutations'
import { newRecordId, useStore } from '@/lib/pocketbase'
import type { CustomerFormData } from '@/types/customers'

export function useCustomers(orgSlug?: string) {
    const params = useLocalSearchParams<{ orgSlug: string }>()
    const slug = orgSlug ?? params.orgSlug

    const [customersCollection, orgsCollection, addressesCollection] = useStore(
        'customers',
        'orgs',
        'addresses'
    )

    const { isLoading, data = [] } = useLiveQuery(
        q =>
            slug
                ? q
                      .from({ org: orgsCollection })
                      .where(({ org }) => eq(org.slug, slug))
                      .join({ customer: customersCollection }, ({ org, customer }) =>
                          eq(customer.org, org.id)
                      )
                      .join(
                          { address: addressesCollection },
                          ({ customer, address }) => eq(customer?.address, address.id),
                          'left'
                      )
                      .orderBy(({ customer }) => customer?.created ?? '', 'desc')
                      .select(({ customer, address }) => ({ customer, address }))
                : undefined,
        [slug]
    )

    // Rehydrate `customer.expand.address` from the joined row so consumers
    // (CustomerCard, JobFormFields, etc.) keep working unchanged. Necessary
    // since pbtsdb 0.4+ no longer refetches on mutation, so the realtime
    // SSE event delivers customer records without expand data.
    const customers = data
        .map(({ customer, address }) =>
            customer
                ? ({
                      ...customer,
                      expand: { ...(customer as { expand?: object }).expand, address },
                  } as typeof customer)
                : null
        )
        .filter((c): c is NonNullable<typeof c> => !!c)

    return {
        customers,
        isPending: isLoading,
    }
}

export function useCustomer(customerSlug: string) {
    const [customersCollection] = useStore('customers')

    const { isLoading, data: customers = [] } = useLiveQuery(q =>
        q
            .from({ customers: customersCollection })
            .where(({ customers }) => eq(customers.identifier, customerSlug))
    )

    return {
        customer: customers[0] ?? null,
        isPending: isLoading,
    }
}

export function useCreateCustomer() {
    const { org: currentOrg } = useCurrentOrg()
    const [customersCollection, addressesCollection] = useStore('customers', 'addresses')

    const mutation = useMutation({
        mutationFn: async (data: CustomerFormData) => {
            if (!currentOrg) throw new Error('No current organization found')

            return await performMutations(function* () {
                const customerId = newRecordId()
                let addressId = ''

                if (isNonBlankAddress(data.expand?.address)) {
                    addressId = newRecordId()
                    yield addressesCollection.insert({
                        ...data.expand.address,
                        id: addressId,
                        org: currentOrg.id,
                    })
                }

                yield customersCollection.insert({
                    id: customerId,
                    name: data.name,
                    identifier: data.identifier,
                    notes: data.notes || '',
                    org: currentOrg.id,
                    address: addressId,
                    inactive_at: '',
                })

                return { customerId, addressId }
            })
        },
    })

    return {
        createCustomer: mutation.mutate,
        isCreating: mutation.isPending,
        mutation,
    }
}

export function useUpdateCustomer() {
    const { org: currentOrg } = useCurrentOrg()
    const [customersCollection] = useStore('customers')

    const mutation = useMutation({
        mutationFn: function* ({
            customerId,
            data,
        }: {
            customerId: string
            data: CustomerFormData
        }) {
            const { transaction: addressTransaction, addressId } = findOrCreateAddress(
                data.expand?.address,
                currentOrg.id
            )

            if (addressTransaction) {
                yield addressTransaction
            }

            yield customersCollection.update(customerId, draft => {
                Object.assign(draft, {
                    name: data.name,
                    identifier: data.identifier,
                    notes: data.notes,
                    address: addressId,
                })
            })
        },
    })

    return {
        updateCustomer: mutation.mutate,
        isUpdating: mutation.isPending,
        mutation,
    }
}

export function useDeleteCustomer() {
    const [customersCollection] = useStore('customers')

    const mutation = useMutation({
        mutationFn: async (customerId: string) => {
            await performMutations(function* () {
                yield customersCollection.delete(customerId)
            })
        },
    })

    return {
        deleteCustomer: mutation.mutate,
        isDeleting: mutation.isPending,
        mutation,
    }
}
